import { useEffect, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { may, useActiveTenant, useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Time zones offered in the picker.
 *
 * A short list rather than every IANA zone: AugRank is a Hyderabad product with
 * Indian and international tenants, all times are `Asia/Kolkata` by default
 * (CLAUDE.md), and a 400-entry select is a worse answer than a handful plus the
 * one the tenant already has. Any zone the API accepts stays valid — the
 * current value is added to the list below if it is not already in it, so a
 * tenant set to something unusual by support does not silently get moved.
 */
const COMMON_TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
]

/**
 * Name and time zone. The slug is shown and not editable, because it is not.
 *
 * Read-only for a member: `tenant:update` is owner and admin only
 * (`api/app/tenancy/roles.py`), and the panel says so rather than presenting
 * fields that will 403 on save.
 */
export function OrganisationPanel(): JSX.Element {
  const { tenant, role } = useActiveTenant()
  const { reload } = useTenants()
  const toast = useToast()
  const editable = may(role, 'tenant:update')

  const [name, setName] = useState(tenant.name)
  const [timezone, setTimezone] = useState(tenant.timezone)

  // Re-seeded when the organisation changes under the panel — a switch from the
  // sidebar keeps this component mounted, and without this the form would show
  // the previous organisation's name against the new one's id.
  useEffect(() => {
    setName(tenant.name)
    setTimezone(tenant.timezone)
  }, [tenant.id, tenant.name, tenant.timezone])

  const save = useMutation((body: { name: string; timezone: string }) =>
    call(() =>
      api.PATCH('/api/v1/tenants/{tenant_id}', {
        params: { path: { tenant_id: tenant.id } },
        body,
      }),
    ),
  )

  const zones = COMMON_TIMEZONES.includes(tenant.timezone)
    ? COMMON_TIMEZONES
    : [tenant.timezone, ...COMMON_TIMEZONES]

  const dirty = name.trim() !== tenant.name || timezone !== tenant.timezone

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({ name: name.trim(), timezone })
    if (!result.ok) return
    reload()
    toast.show({ kind: 'success', title: 'Organisation saved' })
  }

  return (
    <form
      className="ar-card"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__header">
        <h3>Organisation</h3>
        {editable ? null : <span className="ar-badge ar-badge--neutral">Read only</span>}
      </div>

      <div className="ar-card__body ar-stack">
        <InlineError failure={save.state.failure} />
        <TextField
          label="Business name"
          required
          maxLength={120}
          disabled={!editable}
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            save.reset()
          }}
        />
        <TextField
          label="Short name"
          readOnly
          value={tenant.slug}
          help="Fixed when the organisation was created, and used in links and reports."
        />
        <Field
          label="Time zone"
          help="Every schedule, digest and report uses this, never the server clock."
        >
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              disabled={!editable}
              value={timezone}
              onChange={(event) => {
                setTimezone(event.target.value)
                save.reset()
              }}
            >
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          )}
        </Field>
        {editable ? null : (
          <p className="ar-field__help" style={{ margin: 0 }}>
            Your role ({role}) can view the organisation profile. An owner or admin can change it.
          </p>
        )}
      </div>

      {editable ? (
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button
            tone="primary"
            type="submit"
            busy={save.pending}
            busyLabel="Saving"
            disabled={!dirty || name.trim().length === 0}
          >
            Save changes
          </Button>
        </div>
      ) : null}
    </form>
  )
}
